import React from "react";
import { Shield, Bell, Tool, Sun, Moon, Check, Pause, X } from "react-feather";
import { PANEL_ACCENT } from "./CollapsibleCard";

// Small presentational pieces for the inside of detail-pane panels
// (2026-09-12 beautification) - shared by Order / Enquiry / Quotation detail
// so the three read as one design.

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const tint = (hex, alpha) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** One priced service as a small tile: label, big amount, optional working. */
export const AmountTile = ({ label, amount, color = PANEL_ACCENT.money, sub }) => (
  <div
    className="px-3 py-2 rounded"
    style={{ minWidth: 150, background: tint(color, 0.06), border: `1px solid ${tint(color, 0.25)}`, borderTop: `3px solid ${color}` }}
  >
    <div className="text-uppercase fw-semibold" style={{ fontSize: 11, color, letterSpacing: 0.4 }}>{label}</div>
    <div className="fw-bold" style={{ fontSize: 17 }}>{money(amount)}</div>
    {sub && <div className="text-muted" style={{ fontSize: 11.5 }}>{sub}</div>}
  </div>
);

const KIND = {
  GUARDS: { icon: Shield, color: PANEL_ACCENT.guards },
  ALARMS: { icon: Bell, color: PANEL_ACCENT.services },
  OTHER: { icon: Tool, color: PANEL_ACCENT.work },
};

const shortDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : null);

// ---------- Step track ----------------------------------------------------
// A horizontal progress line: each step is done / current / warn (on hold) /
// cancel / todo. Used for an order's life (Confirmed -> ... -> Closed) and an
// installation's (Scheduled -> In Progress -> Completed).
const STEP_STYLE = {
  done: { color: PANEL_ACCENT.money, icon: Check },
  current: { color: PANEL_ACCENT.link, icon: null },
  warn: { color: "#E0A100", icon: Pause },
  cancel: { color: "#C62828", icon: X },
  todo: { color: "#C0C6CF", icon: null },
};

export const StepTrack = ({ steps }) => (
  <div className="d-flex align-items-start" style={{ overflowX: "auto" }}>
    {steps.map((s, i) => {
      const st = STEP_STYLE[s.state] || STEP_STYLE.todo;
      const Icon = st.icon;
      const filled = s.state !== "todo";
      const nextDone = i < steps.length - 1 && steps[i + 1].state !== "todo";
      return (
        <React.Fragment key={s.key}>
          <div className="d-flex flex-column align-items-center text-center" style={{ minWidth: 92, flex: "0 0 auto" }}>
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold"
              style={{
                width: 28, height: 28, fontSize: 12,
                background: filled ? st.color : "#fff",
                color: filled ? "#fff" : st.color,
                border: `2px solid ${st.color}`,
                boxShadow: s.state === "current" ? `0 0 0 4px ${tint(st.color, 0.18)}` : "none",
              }}
            >
              {Icon ? <Icon size={14} /> : i + 1}
            </span>
            <div className="fw-semibold mt-1" style={{ fontSize: 12, color: s.state === "todo" ? "#8A94A6" : "#1D2939" }}>{s.label}</div>
            {s.sub && <div className="text-muted" style={{ fontSize: 11 }}>{s.sub}</div>}
          </div>
          {i < steps.length - 1 && (
            <div
              style={{
                flex: "1 1 24px", minWidth: 24, height: 3, marginTop: 13, borderRadius: 2,
                background: nextDone ? STEP_STYLE.done.color : "#E4E7EC",
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ---------- Guard requirement chips / table -------------------------------
export const ShiftChip = ({ shift }) => {
  const night = shift === "NIGHT";
  const Icon = night ? Moon : Sun;
  const color = night ? "#3949AB" : "#E0A100";
  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1"
      style={{ background: tint(color, 0.12), color, fontSize: 12, fontWeight: 600 }}
    >
      <Icon size={12} /> {night ? "Night" : "Day"}
    </span>
  );
};

export const DeploymentChip = ({ type, startDate, endDate }) => {
  const temp = type === "TEMPORARY";
  const color = temp ? "#E65100" : PANEL_ACCENT.link;
  const dates = temp
    ? `${shortDate(startDate) || "?"} – ${shortDate(endDate) || "?"}`
    : startDate ? `from ${shortDate(startDate)}` : null;
  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1"
      style={{ background: tint(color, 0.1), color, border: `1px solid ${tint(color, 0.3)}`, fontSize: 12, fontWeight: 600 }}
    >
      {temp ? "Temporary" : "Permanent"}
      {dates && <span className="fw-normal" style={{ opacity: 0.85 }}>· {dates}</span>}
    </span>
  );
};

/** Guard requirement lines, shared by Order and Enquiry detail. */
export const GuardRequirementsTable = ({ lines }) => {
  const anyRemarks = lines.some((l) => l.remarks);
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>#</th><th>Service</th><th>Deployment</th><th>Shift</th><th>Schedule</th>
            <th className="text-end">Guards</th>
            {anyRemarks && <th>Remarks</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l, idx) => (
            <tr key={l.requirementId ?? idx}>
              <td className="text-muted">{idx + 1}</td>
              <td className="fw-semibold">{l.serviceTypeName}</td>
              <td><DeploymentChip type={l.deploymentType} startDate={l.startDate} endDate={l.endDate} /></td>
              <td><ShiftChip shift={l.shift} /></td>
              <td className="text-muted">{String(l.schedule || "").replace("_", " ").toLowerCase()}</td>
              <td className="text-end">
                <span className="badge rounded-pill" style={{ background: PANEL_ACCENT.guards, color: "#fff", minWidth: 28 }}>{l.qty}</span>
              </td>
              {anyRemarks && <td className="small">{l.remarks || "-"}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** A requested service as a coloured pill, icon by kind (Guards / Alarms / Other). */
export const ServicePill = ({ name, kind }) => {
  const k = KIND[kind] || KIND.OTHER;
  const Icon = k.icon;
  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1"
      style={{ background: tint(k.color, 0.1), color: k.color, border: `1px solid ${tint(k.color, 0.35)}`, fontSize: 12, fontWeight: 600 }}
    >
      <Icon size={12} /> {name}
    </span>
  );
};
